import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../lib/api';

// Multi-tenant organization context.
//
// Source of truth (when available): `GET /api/me/organizations`.
// Falls back to MOCK_ORGS if the endpoint is unreachable / not yet deployed
// on the user's prod backend — this keeps the SPA usable while the user
// rolls multi-tenant changes from dev to prod.
//
// Shape of an organization (from backend):
// {
//   id, name, type ('nursing_home' | 'assisted_living' | 'hospital' | 'home_care'),
//   address, timezone, status ('active' | 'inactive'),
//   role: 'admin' | 'caregiver' | 'family',  // user's role inside that org
// }

const MOCK_ORGS = [
  {
    id: 'org_e2_hq',
    name: 'E2 Care HQ (mock)',
    type: 'nursing_home',
    address: 'Brīvības iela 100, Rīga',
    timezone: 'Europe/Riga',
    status: 'active',
  },
];

const OrgContext = createContext(null);
const STORAGE_KEY = 'e2care.current_org';

export function OrgProvider({ children }) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const [currentId, setCurrentIdState] = useState(() => localStorage.getItem(STORAGE_KEY));
  // orgVersion bumps every time the active org changes AFTER initial bootstrap.
  // Consumers can either re-key their subtree (Layout does this) or read
  // `orgVersion` as a useEffect dep to refetch their data.
  const [orgVersion, setOrgVersion] = useState(0);
  const bootstrapped = useRef(false);

  // Wrap setCurrentId so version is bumped on user-triggered switches.
  const setCurrentId = useCallback((id) => {
    setCurrentIdState((prev) => {
      if (prev !== id) {
        // Persist immediately so the axios interceptor sees the new value
        // on the very next request triggered by the version bump.
        if (id) localStorage.setItem(STORAGE_KEY, id);
        if (bootstrapped.current) setOrgVersion((v) => v + 1);
      }
      return id;
    });
  }, []);

  // Fetch the user's organizations from the backend after login.
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setOrgs([]);
      setCurrentIdState(null);
      setUsingFallback(false);
      bootstrapped.current = false;
      return;
    }
    setLoading(true);
    (async () => {
      // Super-admins (`admin` role) need the full org catalog so they can switch
      // between any tenant, not just the ones they're personally enrolled in.
      // Everyone else gets just their memberships.
      const isAdmin = user.role === 'admin';
      const endpoint = isAdmin ? '/organizations' : '/me/organizations';
      try {
        const res = await api.get(endpoint);
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        if (list.length === 0) {
          // Authenticated but no orgs returned — fall back so the UI is not blocked.
          setOrgs(MOCK_ORGS.map((o) => ({ ...o, role: user.role || 'caregiver' })));
          setUsingFallback(true);
        } else {
          setOrgs(list);
          setUsingFallback(false);
        }
      } catch (err) {
        if (cancelled) return;
        // Admin endpoint may 403 if the backend doesn't recognise this user as
        // super-admin — fall back to /me/organizations before showing mocks.
        if (isAdmin) {
          try {
            const fallbackRes = await api.get('/me/organizations');
            if (cancelled) return;
            const list = Array.isArray(fallbackRes.data) ? fallbackRes.data : [];
            if (list.length > 0) {
              setOrgs(list);
              setUsingFallback(false);
              return;
            }
          } catch (err2) {
            // eslint-disable-next-line no-console
            console.warn('[OrgContext] /me/organizations fallback failed:', err2?.response?.status || err2?.message);
          }
        }
        // Endpoint not deployed yet (404) or network error — degrade gracefully.
        const decorated = MOCK_ORGS.map((o) => ({ ...o, role: user.role || 'caregiver' }));
        setOrgs(decorated);
        setUsingFallback(true);
        // eslint-disable-next-line no-console
        console.warn(`[OrgContext] ${endpoint} failed, using mock fallback:`, err?.response?.status || err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Pick a sensible default org once the list is loaded, but DO NOT bump version on initial bootstrap.
  useEffect(() => {
    if (!orgs.length) return;
    const stored = localStorage.getItem(STORAGE_KEY);
    const exists = stored && orgs.find((o) => o.id === stored);
    const next = exists ? stored : orgs[0].id;
    if (next !== currentId) {
      setCurrentIdState(next);
      localStorage.setItem(STORAGE_KEY, next);
    } else if (next) {
      localStorage.setItem(STORAGE_KEY, next);
    }
    bootstrapped.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs]);

  const current = orgs.find((o) => o.id === currentId) || null;
  // Source of truth: backend `is_super_admin` boolean on the user payload.
  // Falling back to the legacy `orgs.length > 1` heuristic keeps the SPA
  // usable against older backends that don't expose the field yet.
  const isSuperAdmin = user?.is_super_admin === true || orgs.length > 1;

  return (
    <OrgContext.Provider
      value={{ orgs, current, setCurrentId, isSuperAdmin, loading, usingFallback, orgVersion }}
    >
      {children}
    </OrgContext.Provider>
  );
}

export const useOrg = () => useContext(OrgContext);
